import {
  AlertBanner,
  AlertBannerHeading,
  AlertBannerIcon,
  AlertBannerText,
  Box,
  FormField,
  FormFieldCheckbox,
  FormFieldPhone,
  FormFieldSelect,
  FormFieldSubmit,
  FormFieldTextArea,
} from "@fastly/consistently"
import { Field, Form, Formik } from "formik"
import { useRef, useState, useCallback, useEffect } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import styled from "styled-components"
import { MikanSpan } from "../Mikan"

import { postFormText as postFormText_cn } from "./BaseForm.module.css"

import { useLocale } from "@hooks/use-locale"
import useSubmit from "@utils/useSubmit"
import { confirmation, disclaimer, leadInText as leadInText_cn } from "./ContactSalesForm.module.css"

import { countryJSON } from "./Fields/localized/Country"
import { statesJSON } from "./Fields/localized/State"
import { REGEXES, validateForm } from "./form-utils/validation"

const StyledForm = styled(Box).attrs({ as: Form })`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  column-gap: 16px;

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  textarea:-webkit-autofill,
  textarea:-webkit-autofill:hover,
  textarea:-webkit-autofill:focus,
  select:-webkit-autofill,
  select:-webkit-autofill:hover,
  select:-webkit-autofill:focus {
    border: 0 solid var(--white);
    -webkit-text-fill-color: var(--grey-90);
    box-shadow: 0 0 0px 1000px var(--white) inset;
    transition: background-color 5000s ease-in-out 0s;
  }
`
StyledForm.defaultProps = {
  gridRowGap: "8px",
}

const NameRow = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  > div {
    min-width: unset;
  }
`

const FormFieldMultiSelect = styled(FormFieldSelect)`
  height: 8rem;
  overflow: hidden;

  > select {
    padding-block-start: var(--size-space-lg);
    padding-block-end: var(--size-space-sm);
  }

  &::after {
    display: none;
  }
`

const ContactSalesForm = ({
  labels,
  leadInText,
  postFormText,
  confirmationText,
  formId = "1003",
  requestDemo = false,
  demoOptions,
  productInterestTracking,
  pricingFormDetails,
  setIsSubmitted,
  setFormValues,
  isBookItActive = false,
  isBookItLoaded = false,
  ...props
}) => {
  const [isFocused, setFocused] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState("")
  const recaptchaRef = useRef()
  const formRef = useRef()
  const { locale } = useLocale()
  const typeOfPackage = typeof window !== "undefined" && sessionStorage.getItem("miscstringfield1")
  const nameFields = locale !== "ja" ? ["FirstName", "LastName"] : ["LastName", "FirstName"]

  const { handleSubmit, submitted, submissionFailed } = useSubmit()

  const confirmationRef = useCallback((node) => {
    if (node !== null) {
      window.dataLayer.push({ event: "contact-sales-confirmation" })
    }
  }, [])

  useEffect(() => {
    if (isBookItLoaded && "setFormTarget" in window.LDBookItV2 && formRef.current) {
      window.LDBookItV2.setFormTarget(formRef.current)
    }
  }, [isBookItLoaded])

  return (
    <Formik
      initialValues={{
        FirstName: "",
        LastName: "",
        RequestDemo: "",
        Company: "",
        Country: "",
        State: "",
        Email: "",
        Phone: "",
        Message: "",
        Opt_in: false,
        Opt_In_GDPR__c: false,
        mostRecentProductInterestActivity: productInterestTracking || "",
        pricingFormDetails: pricingFormDetails || "",
        formId: formId,
        formVid: formId,
        pageLanguage: locale,
        miscstringfield1: typeOfPackage,
      }}
      onSubmit={(values, { setSubmitting }) => {
        handleSubmit(values, setSubmitting, recaptchaRef)
        if (typeof setFormValues === "function") setFormValues(values)
        typeof window !== "undefined" && window.sessionStorage.setItem("email", values.Email)
        // Track conversion
        window.LUX?.addData("contactsales", true)
      }}
      validate={(values) => {
        const valuesToTest =
          locale === "ja"
            ? ["FirstName", "LastName", "Email", "Company", "Phone", "Message", "Country"]
            : ["FirstName", "LastName", "Email", "Company", "Phone", "Message"]

        const defaultMessage = labels[`field.error.required`]
        const errors = validateForm(valuesToTest, values, defaultMessage, {
          Email: { message: labels[`field.error.email`], regex: REGEXES.email },
        })

        return errors
      }}
    >
      {({ isSubmitting, errors, touched }) => (
        <StyledForm id="contact-form-container" ref={formRef} {...props} onFocus={() => setFocused(true)}>
          {submitted ? (
            setIsSubmitted ? (
              setIsSubmitted(true)
            ) : (
              <div className={confirmation} ref={confirmationRef}>
                <MikanSpan>{confirmationText.childMarkdownRemark.html}</MikanSpan>
              </div>
            )
          ) : (
            <>
              {submissionFailed && (
                <AlertBanner type="error">
                  <AlertBannerIcon />
                  <AlertBannerHeading data-test-id="banner-heading">
                    {labels["form-submission-error-label"]}
                  </AlertBannerHeading>
                  <AlertBannerText data-test-id="banner-body">
                    {labels["form-submission-error-message"]}
                  </AlertBannerText>
                </AlertBanner>
              )}
              {leadInText && (
                <div className={leadInText_cn}>
                  <MikanSpan>{leadInText.childMarkdownRemark.html}</MikanSpan>
                </div>
              )}

              <NameRow>
                <Field name={nameFields[0]}>
                  {({ field, meta }) => (
                    <FormField
                      label={<MikanSpan text={labels[`field.${nameFields[0].toLowerCase()}`]} />}
                      placeholder={labels[`field.${nameFields[0].toLowerCase()}.placeholder.verbose`]}
                      {...meta}
                      {...field}
                    />
                  )}
                </Field>
                <Field name={nameFields[1]}>
                  {({ field, meta }) => (
                    <FormField
                      label={<MikanSpan text={labels[`field.${nameFields[1].toLowerCase()}`]} />}
                      placeholder={labels[`field.${nameFields[1].toLowerCase()}.placeholder.verbose`]}
                      {...meta}
                      {...field}
                    />
                  )}
                </Field>
              </NameRow>

              {requestDemo ? (
                <Field name="RequestDemo">
                  {({ field, meta }) => (
                    <FormFieldMultiSelect
                      label={labels["please-select-which-products-you-would-like-to-get-a-demo-of"]}
                      options={demoOptions.map((option) => ({ value: option.text, display: option.text }))}
                      multiple
                      {...meta}
                      {...field}
                    />
                  )}
                </Field>
              ) : null}

              <Field name="Email">
                {({ field, meta }) => (
                  <FormField
                    label={<MikanSpan text={labels["field.email.verbose"]} />}
                    placeholder={labels["field.email.placeholder.verbose"]}
                    {...meta}
                    {...field}
                  />
                )}
              </Field>

              <Field name="Company">
                {({ field, meta }) => (
                  <FormField
                    label={<MikanSpan text={labels["field.company"]} />}
                    placeholder={labels["field.company.placeholder.verbose"]}
                    {...meta}
                    {...field}
                  />
                )}
              </Field>

              {locale === "ja" && (
                <Field
                  name="Country"
                  validate={(country) => {
                    setSelectedCountry(country)
                  }}
                >
                  {({ field, meta }) => (
                    <FormFieldSelect
                      label={<MikanSpan text={labels["field.country"]} />}
                      options={countryJSON(locale)}
                      {...meta}
                      {...field}
                    />
                  )}
                </Field>
              )}

              {["JP", "US"].includes(selectedCountry) && (
                <Field name="State">
                  {({ field, meta }) => (
                    <FormFieldSelect
                      label={<MikanSpan text={labels["field.state"]} />}
                      options={statesJSON(selectedCountry, locale)}
                      {...meta}
                      {...field}
                    />
                  )}
                </Field>
              )}

              <Field name="Phone">
                {({ field, meta }) => (
                  <FormFieldPhone
                    placeholder={labels["field.phone.placeholder.verbose"]}
                    label={<MikanSpan text={labels["field.phone"]} />}
                    error={errors["Phone"]}
                    touched={touched["Phone"]}
                    {...meta}
                    {...field}
                  />
                )}
              </Field>

              <Field name="Message">
                {({ field, meta }) => (
                  <FormFieldTextArea
                    label={<MikanSpan text={labels["field.message"]} />}
                    placeholder={labels["field.message.placeholder"]}
                    {...meta}
                    {...field}
                  />
                )}
              </Field>

              <Field name="Opt_In_GDPR__c">
                {({ field, meta }) => (
                  <FormFieldCheckbox label={<MikanSpan text={labels["field.optin.short"]} />} {...meta} {...field} />
                )}
              </Field>
              {!!isBookItActive && <Field type="hidden" name="BookItField" id="BookItField" />}
              <FormFieldSubmit variant="main" loading={isSubmitting}>
                <MikanSpan text={labels["field.submit.submit"]} />
              </FormFieldSubmit>
              <div className={`${postFormText_cn} ${disclaimer}`}>
                <MikanSpan>{postFormText.childMarkdownRemark.html}</MikanSpan>
              </div>
            </>
          )}
          {isFocused && (
            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey={process.env.GATSBY_RECAPTCHA_SITEKEY || "6LcQLjEUAAAAAMGtzNz-AGhMH6vrXBbBTw0h9TLO"}
              badge="inline"
            />
          )}
        </StyledForm>
      )}
    </Formik>
  )
}

export default ContactSalesForm
